/**
 * CometChat Provider for the Admin Panel.
 *
 * Initializes the CometChat JavaScript SDK and logs in the admin user
 * when authenticated. Uses the same user.id as UID for cross-platform
 * compatibility with the companion app.
 *
 * Provider chain: Google → Theme → Permission → SocketIO → CometChat → MyAdmin
 */

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import Logger from '../utils/Logger';

const APP_ID = import.meta.env.VITE_COMETCHAT_APP_ID || '';
const AUTH_KEY = import.meta.env.VITE_COMETCHAT_AUTH_KEY || '';
const REGION = import.meta.env.VITE_COMETCHAT_REGION || 'us';

interface CometChatContextType {
  isReady: boolean;
  isLoggedIn: boolean;
}

const CometChatCtx = createContext<CometChatContextType>({
  isReady: false,
  isLoggedIn: false,
});

export const useCometChat = () => useContext(CometChatCtx);

export function CometChatProvider({ children }: { children: React.ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const initRef = useRef(false);

  // Initialize SDK once
  useEffect(() => {
    if (initRef.current || !APP_ID) return;
    initRef.current = true;

    const appSettings = new CometChat.AppSettingsBuilder()
      .subscribePresenceForAllUsers()
      .setRegion(REGION)
      .autoEstablishSocketConnection(true)
      .build();

    CometChat.init(APP_ID, appSettings).then(
      () => {
        Logger.info('[CometChat] Admin SDK initialized');
        setIsReady(true);
      },
      (err: unknown) => Logger.error('[CometChat] Admin init failed:', err),
    );
  }, []);

  // Login when auth token available
  useEffect(() => {
    if (!isReady) return;

    // Read user from React-Admin's localStorage auth
    const authStr = localStorage.getItem('auth');
    if (!authStr) return;

    let userId: string | undefined;
    try {
      const auth = JSON.parse(authStr);
      userId = auth?.id;
    } catch {
      return;
    }

    if (!userId) return;

    CometChat.login(userId, AUTH_KEY).then(
      () => {
        Logger.info('[CometChat] Admin logged in as', userId);
        setIsLoggedIn(true);
      },
      (err: any) => {
        if (err?.code === 'ERR_UID_ALREADY_LOGGED_IN') {
          setIsLoggedIn(true);
          return;
        }
        Logger.error('[CometChat] Admin login failed:', err);
      },
    );
  }, [isReady]);

  return (
    <CometChatCtx.Provider value={{ isReady, isLoggedIn }}>
      {children}
    </CometChatCtx.Provider>
  );
}
